import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "tailwindcss/tailwind.css";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  PencilSquareIcon,
  PlusIcon,
  PlusCircleIcon,
  TrashIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import { BASE_URL } from "../../config";

const BANK_ACCOUNT_NUMBER_MAX_LENGTH = 20;

const parseDateInput = (value) => {
  if (!value) return null;

  const [year, month, day] = String(value)
    .split("-")
    .map((part) => Number(part));

  if (!year || !month || !day) return null;

  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addMonthsWithRollover = (baseDate, monthsToAdd) => {
  const target = new Date(
    baseDate.getFullYear(),
    baseDate.getMonth(),
    baseDate.getDate()
  );

  target.setMonth(target.getMonth() + monthsToAdd);

  return target;
};

const getEffectiveDateToFromExpiryDate = (expiryDateValue) => {
  const expiryDate = parseDateInput(expiryDateValue);
  if (!expiryDate) return "";

  const effectiveDateTo = new Date(expiryDate);
  effectiveDateTo.setDate(effectiveDateTo.getDate() - 1);

  return formatDateInput(effectiveDateTo);
};

const formatApiErrorMessage = (error) => {
  const responseData = error?.response?.data;

  if (responseData) {
    if (typeof responseData === "string") return responseData;
    return JSON.stringify(responseData);
  }

  return error?.message || "Unknown error";
};

const isTruthyFlag = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;

  const normalizedValue = String(value ?? "")
    .trim()
    .toLowerCase();
  return ["1", "true", "yes", "y"].includes(normalizedValue);
};

const isRequiredValueEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

const UploadedDocumentUrlPanel = ({
  url,
  downloadLabel,
  onRegenerate,
}) => {
  if (!url) return null;

  return (
    <div className="mt-3 space-y-2">
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center space-x-1 text-blue-600 hover:text-blue-800"
        >
          <ArrowDownTrayIcon className="h-5 w-5" />
          <span className="font-medium">{downloadLabel}</span>
        </a>

        <button
          type="button"
          onClick={onRegenerate}
          className="flex items-center space-x-1 text-blue-600 hover:text-blue-800"
        >
          <ArrowPathIcon className="h-5 w-5" />
          <span>Regenerate URL</span>
        </button>
      </div>

      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="block break-all rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-500 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
      >
        <span className="mb-1 block font-semibold text-gray-700">URL:</span>
        {url}
      </a>
    </div>
  );
};

const CorporateEnrollmentForm = () => {
  const { corpCode } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!corpCode;
  const [isLoading, setIsLoading] = useState(false);
  const [corpAddressLoading, setCorpAddressLoading] = useState({
    region: false,
    province: false,
    towncity: false,
    barangay: false,
  });
  const [billAddressLoading, setBillAddressLoading] = useState({
    region: false,
    province: false,
    towncity: false,
    barangay: false,
  });

  const [formData, setFormData] = useState({
    corp_code: "",
    corp_prefix: "",
    corp_name: "",
    actual_corp_name: "",
    corp_card_name: "",
    licensed_name: "",
    corp_industry_code: "",
    corp_industry_others: "",
    corp_type_code: "",
    phic_no: "",
    min_age: "",
    max_age: "",
    remarks: "",
    cobrand_layer: "1",
    cobrand_tag: false,
    spec_exp: false,
    effective_date: "",
    effective_date_to: "",
    expiry_date: "",
    expiry_duration: "y",
    bill_duedate: "",
    auto_deduct_tag: false,
    bank_acc_name: "",
    bank_no: "",
    bank_name: "",
    bank_branch: "",
    tin: "",
    withheld: false,
    rep_tin: "",
    rep_name: "",
    electronic_signature_upload_id: "",
    electronic_signature_file_name: "",
    electronic_signature_signed_url: "",
    electronic_signature_expires_at: "",
    rep_position: "",
    secretary_cert_upload_id: "",
    secretary_cert_file_name: "",
    secretary_cert_signed_url: "",
    secretary_cert_expires_at: "",
    payment_setup: "0",
    admin_fee: "",
    cash_bond: "",
    revolving_fund_amt: "",
    utilized_amt: "0",
    stale_amt: "0",
    first_warning_amount: "",
    threshold_amt: "",
    ceo_name: "",
    coo_name: "",
    corp_careof: "",
    corp_careof_designation: "",
    tel_no: "",
    fax_no: "",
    mobtel: "",
    email_add: "",
    email_recipient: "",
    corp_streetno_bldg: "",
    corp_region: "",
    corp_province: "",
    corp_towncity: "",
    corp_barangay: "",
    corp_postalcode: "",
    bill_streetno_bldg: "",
    bill_region: "",
    bill_province: "",
    bill_towncity: "",
    bill_barangay: "",
    bill_postalcode: "",
    corporate_logo_upload_id: "",
    corporate_logo_file_name: "",
    corporate_logo_signed_url: "",
    corporate_logo_expires_at: "",
    sob_file_upload_id: "",
    sob_file_file_name: "",
    sob_file_signed_url: "",
    sob_file_expires_at: "",
    main_sub: "main",
    main_corp: "",
    status_flag: "0",
    fund_contacts: [],
    fund_mgmt_flag: false,
  });
  const [fundForm, setFundForm] = useState({
    fund_name: "",
    fund_mobile: "",
    fund_email: "",
    fund_mgmt_flag: false,
  });
  const [industries, setIndustries] = useState([]);
  const [corpPrefixes, setCorpPrefixes] = useState([]);
  const [corpRegions, setCorpRegions] = useState([]);
  const [corpProvinces, setCorpProvinces] = useState([]);
  const [corpTownCities, setCorpTownCities] = useState([]);
  const [corpBarangays, setCorpBarangays] = useState([]);
  const [billRegions, setBillRegions] = useState([]);
  const [billProvinces, setBillProvinces] = useState([]);
  const [billTownCities, setBillTownCities] = useState([]);
  const [billBarangays, setBillBarangays] = useState([]);
  const [mainCorporations, setMainCorporations] = useState([]);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState("information");
  const [logoPreview, setLogoPreview] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [autoCalculateExpiryDate, setAutoCalculateExpiryDate] = useState(
    !isEditMode
  );

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
  });

  // Calculate effective_date_to and expiry_date
  useEffect(() => {
    if (!autoCalculateExpiryDate) {
      return;
    }

    const effectiveDate = parseDateInput(formData.effective_date);

    if (!effectiveDate) {
      setFormData((prev) => {
        if (!prev.effective_date_to && !prev.expiry_date) {
          return prev;
        }

        return {
          ...prev,
          effective_date_to: "",
          expiry_date: "",
        };
      });
      return;
    }

    const monthsToAdd = formData.expiry_duration === "m" ? 6 : 12;
    const expiryDate = addMonthsWithRollover(effectiveDate, monthsToAdd);
    const effectiveDateTo = new Date(expiryDate);
    effectiveDateTo.setDate(effectiveDateTo.getDate() - 1);

    const nextEffectiveDateTo = formatDateInput(effectiveDateTo);
    const nextExpiryDate = formatDateInput(expiryDate);

    setFormData((prev) => {
      if (
        prev.effective_date_to === nextEffectiveDateTo &&
        prev.expiry_date === nextExpiryDate
      ) {
        return prev;
      }

      return {
        ...prev,
        effective_date_to: nextEffectiveDateTo,
        expiry_date: nextExpiryDate,
      };
    });
  }, [
    autoCalculateExpiryDate,
    formData.effective_date,
    formData.expiry_duration,
  ]);

  // Fetch main corporate data when main_sub is sub
  useEffect(() => {
    if (formData.main_sub === "sub" && formData.main_corp) {
      const fetchMainCorp = async () => {
        try {
          setIsLoading(true);
          const response = await axios.get(
            `${BASE_URL}/corporates/${formData.main_corp}`,
            {
              headers: getAuthHeaders(),
            }
          );
          const mainCorp = response.data.data;
          setFormData((prev) => ({
            ...prev,
            corp_prefix: mainCorp.corp_prefix,
            cash_bond: mainCorp.cash_bond ?? "",
            revolving_fund_amt: mainCorp.revolving_fund_amt ?? "",
            first_warning_amount: mainCorp.first_warning_amount ?? "",
            threshold_amt: mainCorp.threshold_amt ?? "",
            admin_fee: mainCorp.admin_fee ?? "",
            payment_setup: mainCorp.payment_setup ?? "0",
            utilized_amt: mainCorp.utilized_amt ?? "0",
            stale_amt: mainCorp.stale_amt ?? "0",
          }));
        } catch (error) {
          toast.error(
            "Failed to fetch main corporate data: " +
              (error.response?.data?.message || error.message)
          );
        } finally {
          setIsLoading(false);
        }
      };
      fetchMainCorp();
    }
  }, [formData.main_sub, formData.main_corp]);

  // Fetch corporate details for edit mode
  useEffect(() => {
    if (isEditMode) {
      const fetchCorporate = async () => {
        setIsLoading(true);
        try {
          const response = await axios.get(
            `${BASE_URL}/corporates/${corpCode}`,
            {
              headers: getAuthHeaders(),
            }
          );
          const corporate = response.data.data;
          setAutoCalculateExpiryDate(false);
          setFormData({
            ...corporate,
            fund_mgmt_flag: isTruthyFlag(corporate.fund_mgmt_flag),
            effective_date_to: getEffectiveDateToFromExpiryDate(
              corporate.expiry_date
            ),
            fund_contacts: corporate.fund_contacts || [],
            expiry_duration: corporate.expiry_date
              ? new Date(corporate.expiry_date) -
                  new Date(corporate.effective_date) >
                180 * 24 * 60 * 60 * 1000
                ? "y"
                : "m"
              : "y",
          });

          if (response.data.data.corporate_logo_signed_url) {
            setLogoPreview(response.data.data.corporate_logo_signed_url);
          }
          if (response.data.data.corp_region) {
            fetchCorpProvinces(response.data.data.corp_region);
          }
          if (response.data.data.corp_province) {
            fetchCorpTownCities(response.data.data.corp_province);
          }
          if (response.data.data.corp_towncity) {
            fetchCorpBarangays(response.data.data.corp_towncity);
          }
          if (response.data.data.bill_region) {
            fetchBillProvinces(response.data.data.bill_region);
          }
          if (response.data.data.bill_province) {
            fetchBillTownCities(response.data.data.bill_province);
          }
          if (response.data.data.bill_towncity) {
            fetchBillBarangays(response.data.data.bill_towncity);
          }
        } catch (error) {
          toast.error(
            "Failed to fetch corporate details: " +
              (error.response?.data?.message || error.message)
          );
          setTimeout(() => navigate("/corporate"), 2000);
        } finally {
          setIsLoading(false);
          console.log(formData);
        }
      };
      fetchCorporate();
    }
  }, [isEditMode, corpCode, navigate]);

  // Fetch industries
  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${BASE_URL}/corporates/industries`, {
          headers: getAuthHeaders(),
        });
        // Ensure "Others" is included in the industries list
        const industriesWithOthers = [
          ...response.data.data.map((i) => ({
            value: i.corp_industry_code,
            label: i.corp_industry_desc,
          })),
          { value: "O", label: "Others" }, // Explicitly add "Others" option
        ];
        setIndustries(industriesWithOthers);
      } catch (error) {
        toast.error(
          "Failed to load industries: " +
            (error.response?.data?.message || error.message)
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchIndustries();
  }, []);

  // Fetch corp prefixes
  useEffect(() => {
    const fetchCorpPrefixes = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(
          `${BASE_URL}/corporates/corp-prefixes`,
          {
            headers: getAuthHeaders(),
          }
        );
        setCorpPrefixes(
          response.data.data.map((prefix) => ({
            value: prefix,
            label: prefix,
          }))
        );
      } catch (error) {
        toast.error(
          "Failed to load corp prefixes: " +
            (error.response?.data?.message || error.message)
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchCorpPrefixes();
  }, []);

  // Fetch regions for corporate and billing addresses
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        setIsLoading(true);
        setCorpAddressLoading((prev) => ({ ...prev, region: true }));
        setBillAddressLoading((prev) => ({ ...prev, region: true }));
        const response = await axios.get(
          `${BASE_URL}/address/get-ref-address`,
          {
            headers: getAuthHeaders(),
          }
        );
        const regions = response.data.data.map((region) => ({
          value: region.region_code,
          label: region.region,
        }));
        setCorpRegions(regions);
        setBillRegions(regions);
      } catch (error) {
        toast.error(
          "Failed to load regions: " +
            (error.response?.data?.message || error.message)
        );
      } finally {
        setIsLoading(false);
        setCorpAddressLoading((prev) => ({ ...prev, region: false }));
        setBillAddressLoading((prev) => ({ ...prev, region: false }));
      }
    };
    fetchRegions();
  }, []);

  // Fetch main corporations
  useEffect(() => {
    const fetchMainCorporations = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${BASE_URL}/corporates`, {
          headers: getAuthHeaders(),
          params: { limit: 1000, sort_by: "corp_name", sort_direction: "asc" },
        });
        setMainCorporations(
          response.data.corporates.map((c) => ({
            value: c.corp_code,
            label: c.corp_name,
          }))
        );
      } catch (error) {
        toast.error(
          "Failed to load main corporations: " +
            (error.response?.data?.message || error.message)
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchMainCorporations();
  }, []);

  // Fetch provinces for corporate address
  const fetchCorpProvinces = async (regionCode) => {
    if (!regionCode) return;
    try {
      setCorpAddressLoading((prev) => ({ ...prev, province: true }));
      const response = await axios.get(`${BASE_URL}/address/get-ref-address`, {
        headers: getAuthHeaders(),
        params: { region_code: regionCode },
      });
      const provinces = response.data.data.map((province) => ({
        value: province.province_code,
        label: province.province,
      }));
      setCorpProvinces(provinces);
    } catch (error) {
      console.error(
        `Failed to fetch corporate provinces:`,
        error.response?.data?.message || error.message
      );
      toast.error(
        "Failed to load corporate provinces: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setCorpAddressLoading((prev) => ({ ...prev, province: false }));
    }
  };

  // Fetch provinces for billing address
  const fetchBillProvinces = async (regionCode) => {
    if (!regionCode) return;
    try {
      setBillAddressLoading((prev) => ({ ...prev, province: true }));
      const response = await axios.get(`${BASE_URL}/address/get-ref-address`, {
        headers: getAuthHeaders(),
        params: { region_code: regionCode },
      });
      const provinces = response.data.data.map((province) => ({
        value: province.province_code,
        label: province.province,
      }));
      setBillProvinces(provinces);
    } catch (error) {
      console.error(
        `Failed to fetch billing provinces:`,
        error.response?.data?.message || error.message
      );
      toast.error(
        "Failed to load billing provinces: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setBillAddressLoading((prev) => ({ ...prev, province: false }));
    }
  };

  // Fetch town/cities for corporate address
  const fetchCorpTownCities = async (provinceCode) => {
    if (!provinceCode) return;
    try {
      setCorpAddressLoading((prev) => ({ ...prev, towncity: true }));
      const response = await axios.get(`${BASE_URL}/address/get-ref-address`, {
        headers: getAuthHeaders(),
        params: { province_code: provinceCode },
      });
      const townCities = response.data.data.map((city) => ({
        value: city.city_code,
        label: city.city,
      }));
      setCorpTownCities(townCities);
    } catch (error) {
      console.error(
        `Failed to fetch corporate town/cities:`,
        error.response?.data?.message || error.message
      );
      toast.error(
        "Failed to load corporate town/cities: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setCorpAddressLoading((prev) => ({ ...prev, towncity: false }));
    }
  };

  // Fetch town/cities for billing address
  const fetchBillTownCities = async (provinceCode) => {
    if (!provinceCode) return;
    try {
      setBillAddressLoading((prev) => ({ ...prev, towncity: true }));
      const response = await axios.get(`${BASE_URL}/address/get-ref-address`, {
        headers: getAuthHeaders(),
        params: { province_code: provinceCode },
      });
      const townCities = response.data.data.map((city) => ({
        value: city.city_code,
        label: city.city,
      }));
      setBillTownCities(townCities);
    } catch (error) {
      console.error(
        `Failed to fetch billing town/cities:`,
        error.response?.data?.message || error.message
      );
      toast.error(
        "Failed to load billing town/cities: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setBillAddressLoading((prev) => ({ ...prev, towncity: false }));
    }
  };

  // Fetch barangays for corporate address
  const fetchCorpBarangays = async (cityCode) => {
    if (!cityCode) return;
    try {
      setCorpAddressLoading((prev) => ({ ...prev, barangay: true }));
      const response = await axios.get(`${BASE_URL}/address/get-ref-address`, {
        headers: getAuthHeaders(),
        params: { city_code: cityCode },
      });
      const barangays = response.data.data.map((barangay) => ({
        value: barangay.baranggay_code,
        label: barangay.baranggay,
        postcode: barangay.postcode || "",
      }));
      setCorpBarangays(barangays);
    } catch (error) {
      console.error(
        `Failed to fetch corporate barangays:`,
        error.response?.data?.message || error.message
      );
      toast.error(
        "Failed to load corporate barangays: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setCorpAddressLoading((prev) => ({ ...prev, barangay: false }));
    }
  };

  // Fetch barangays for billing address
  const fetchBillBarangays = async (cityCode) => {
    if (!cityCode) return;
    try {
      setBillAddressLoading((prev) => ({ ...prev, barangay: true }));
      const response = await axios.get(`${BASE_URL}/address/get-ref-address`, {
        headers: getAuthHeaders(),
        params: { city_code: cityCode },
      });
      const barangays = response.data.data.map((barangay) => ({
        value: barangay.baranggay_code,
        label: barangay.baranggay,
        postcode: barangay.postcode || "",
      }));
      setBillBarangays(barangays);
    } catch (error) {
      console.error(
        `Failed to fetch billing barangays:`,
        error.response?.data?.message || error.message
      );
      toast.error(
        "Failed to load billing barangays: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setBillAddressLoading((prev) => ({ ...prev, barangay: false }));
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue =
      name === "bank_no"
        ? value.slice(0, BANK_ACCOUNT_NUMBER_MAX_LENGTH)
        : value;

    if (name === "expiry_date") {
      setAutoCalculateExpiryDate(false);
      setFormData((prev) => ({
        ...prev,
        expiry_date: value,
        effective_date_to: getEffectiveDateToFromExpiryDate(value),
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : nextValue,
    }));
  };

  // Handle select changes
  const handleSelectChange = (name, selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : "",
    }));
    if (name === "corp_industry_code") {
      setFormData((prev) => ({
        ...prev,
        corp_industry_others:
          selectedOption?.value === "O" ? prev.corp_industry_others : "",
      }));
    }
    if (name === "corp_region") {
      setFormData((prev) => ({
        ...prev,
        corp_province: "",
        corp_towncity: "",
        corp_barangay: "",
        corp_postalcode: "",
      }));
      setCorpProvinces([]);
      setCorpTownCities([]);
      setCorpBarangays([]);
      fetchCorpProvinces(selectedOption?.value);
    } else if (name === "corp_province") {
      setFormData((prev) => ({
        ...prev,
        corp_towncity: "",
        corp_barangay: "",
        corp_postalcode: "",
      }));
      setCorpTownCities([]);
      setCorpBarangays([]);
      fetchCorpTownCities(selectedOption?.value);
    } else if (name === "corp_towncity") {
      setFormData((prev) => ({
        ...prev,
        corp_barangay: "",
        corp_postalcode: "",
      }));
      setCorpBarangays([]);
      fetchCorpBarangays(selectedOption?.value);
    } else if (name === "corp_barangay") {
      const barangay = corpBarangays.find(
        (b) => b.value === selectedOption?.value
      );
      setFormData((prev) => ({
        ...prev,
        corp_postalcode: barangay?.postcode || "",
      }));
    } else if (name === "bill_region") {
      setFormData((prev) => ({
        ...prev,
        bill_province: "",
        bill_towncity: "",
        bill_barangay: "",
        bill_postalcode: "",
      }));
      setBillProvinces([]);
      setBillTownCities([]);
      setBillBarangays([]);
      fetchBillProvinces(selectedOption?.value);
    } else if (name === "bill_province") {
      setFormData((prev) => ({
        ...prev,
        bill_towncity: "",
        bill_barangay: "",
        bill_postalcode: "",
      }));
      setBillTownCities([]);
      setBillBarangays([]);
      fetchBillTownCities(selectedOption?.value);
    } else if (name === "bill_towncity") {
      setFormData((prev) => ({
        ...prev,
        bill_barangay: "",
        bill_postalcode: "",
      }));
      setBillBarangays([]);
      fetchBillBarangays(selectedOption?.value);
    } else if (name === "bill_barangay") {
      const barangay = billBarangays.find(
        (b) => b.value === selectedOption?.value
      );
      setFormData((prev) => ({
        ...prev,
        bill_postalcode: barangay?.postcode || "",
      }));
    } else if (name === "main_corp") {
      setFormData((prev) => ({
        ...prev,
        main_corp: selectedOption?.value || "",
      }));
    } else if (name === "corp_prefix") {
      setFormData((prev) => ({
        ...prev,
        corp_prefix: selectedOption?.value || "",
      }));
    }
  };

  // Handle file uploads
  const handleFileUpload = async (e, uploaderType) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    formDataUpload.append("uploader_type", uploaderType);
    if (isEditMode && corpCode) {
      formDataUpload.append("corporate_code", corpCode);
    }

    try {
      setIsLoading(true);
      const response = await axios.post(
        `${BASE_URL}/corporates/document/upload`,
        formDataUpload,
        {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "multipart/form-data",
          },
        }
      );
      const {
        upload_id,
        original_file_name,
        signed_url,
        url_expires_at,
        public_share_token,
        public_share_url,
        public_share_expires_at,
      } = response.data;
      const displayUrl = public_share_url || signed_url;
      setFormData((prev) => ({
        ...prev,
        [`${uploaderType}_upload_id`]: upload_id,
        [`${uploaderType}_file_name`]: original_file_name,
        [`${uploaderType}_signed_url`]: displayUrl,
        [`${uploaderType}_expires_at`]: url_expires_at,
        [`${uploaderType}_public_share_token`]: public_share_token,
        [`${uploaderType}_public_share_url`]: public_share_url,
        [`${uploaderType}_public_share_expires_at`]: public_share_expires_at,
      }));
      if (uploaderType === "corporate_logo") {
        setLogoPreview(displayUrl);
      }
      toast.success("File uploaded successfully");
    } catch (error) {
      toast.error(
        `Failed to upload file: ${formatApiErrorMessage(error)}`
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Regenerate signed URL
  const handleRegenerateUrl = async (uploadId, uploaderType) => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${BASE_URL}/corporates/document/regenerate-url`,
        { upload_id: uploadId },
        {
          headers: getAuthHeaders(),
        }
      );
      const {
        signed_url,
        url_expires_at,
        public_share_token,
        public_share_url,
        public_share_expires_at,
      } = response.data;
      const displayUrl = public_share_url || signed_url;
      setFormData((prev) => ({
        ...prev,
        [`${uploaderType}_signed_url`]: displayUrl,
        [`${uploaderType}_expires_at`]: url_expires_at,
        [`${uploaderType}_public_share_token`]: public_share_token,
        [`${uploaderType}_public_share_url`]: public_share_url,
        [`${uploaderType}_public_share_expires_at`]: public_share_expires_at,
      }));
      if (uploaderType === "corporate_logo") {
        setLogoPreview(displayUrl);
      }
      toast.success("URL regenerated successfully");
    } catch (error) {
      toast.error(
        "Failed to regenerate URL: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    const requiredFields = [
      "corp_name",
      "actual_corp_name",
      "corp_card_name",
      "corp_type_code",
      "phic_no",
      "min_age",
      "max_age",
      "cobrand_layer",
      "effective_date",
      "expiry_date",
      "bill_duedate",
      "bank_acc_name",
      "bank_no",
      "bank_name",
      "bank_branch",
      "tin",
      "corp_careof",
      "corp_careof_designation",
      "email_recipient",
      "corp_streetno_bldg",
      "corp_region",
      "corp_province",
      "corp_towncity",
      "corp_barangay",
      "corp_postalcode",
      "revolving_fund_amt",
      "threshold_amt",
      "main_sub",
    ];
    const newErrors = {};
    const missingFields = []; // Array to collect missing field names for the toast

    requiredFields.forEach((field) => {
      if (isRequiredValueEmpty(formData[field])) {
        newErrors[field] = "This field is required";
        missingFields.push(field); // Add to missing fields array
      }
    });

    const isFundManagementEnabled = isTruthyFlag(formData.fund_mgmt_flag);

    // Fund contacts are required only when fund management flag is enabled
    if (
      isFundManagementEnabled &&
      (!formData.fund_contacts || formData.fund_contacts.length === 0)
    ) {
      newErrors.fund_contacts = "At least one fund contact is required";
      missingFields.push("fund_contacts");
    }


    if (formData.corp_industry_code === "O" && !formData.corp_industry_others) {
      newErrors.corp_industry_others =
        "This field is required when industry is Others";
        missingFields.push("corp_industry_others");
    }
    if (formData.withheld) {
      [
        "rep_tin",
        "rep_name",
        "rep_position",
        "electronic_signature_upload_id",
        "secretary_cert_upload_id",
      ].forEach((field) => {
        if (!formData[field]) {
          newErrors[field] = "This field is required when withheld is checked";
          missingFields.push(field);
        }
      });
    }

    // required na daw kahit hindi naka true si fund_mgmt_flag
    // if (formData.fund_mgmt_flag) {
    // ["fund_name", "fund_mobile", "fund_email"].forEach((field) => {
    //   if (!formData[field]) {
    //     newErrors[field] =
    //       "This field is required when Automated Fund Management is checked";
    //       missingFields.push(field);
    //   }
    // });
    // }

    if (formData.main_sub === "sub" && !formData.main_corp) {
      newErrors.main_corp =
        "This field is required when Sub Account is selected";
        missingFields.push("main_corp");
    }

    const bankNoValue = String(formData.bank_no || "");
    if (bankNoValue.length > BANK_ACCOUNT_NUMBER_MAX_LENGTH) {
      newErrors.bank_no = `Account Number must not exceed ${BANK_ACCOUNT_NUMBER_MAX_LENGTH} characters.`;
    }

    const ageValidationMessage =
      "Acceptance Age must be less than or equal to Termination Age.";

    if (
      !isRequiredValueEmpty(formData.min_age) &&
      !isRequiredValueEmpty(formData.max_age)
    ) {
      const acceptanceAge = Number(formData.min_age);
      const terminationAge = Number(formData.max_age);

      if (
        Number.isFinite(acceptanceAge) &&
        Number.isFinite(terminationAge) &&
        acceptanceAge > terminationAge
      ) {
        newErrors.min_age = ageValidationMessage;
        newErrors.max_age = ageValidationMessage;
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);

      if (
        newErrors.min_age === ageValidationMessage ||
        newErrors.max_age === ageValidationMessage
      ) {
        toast.error(ageValidationMessage);
        return;
      }

      if (
        newErrors.bank_no &&
        bankNoValue.length > BANK_ACCOUNT_NUMBER_MAX_LENGTH
      ) {
        toast.error(newErrors.bank_no);
        return;
      }

      if (missingFields.length > 0) {
        const errorMessage = `Please fill in the following required fields: ${missingFields.join(", ")}`;
        toast.error(errorMessage);
        return;
      }

      toast.error(Object.values(newErrors)[0]);
      return;
    }

    try {
      setIsLoading(true);
      const nameCheck = await axios.post(
        `${BASE_URL}/corporates/check-existing`,
        {
          corp_name: formData.corp_name,
          corp_card_name: formData.corp_card_name,
        },
        { headers: getAuthHeaders() }
      );

      if (
        nameCheck.data.exists &&
        (!isEditMode || nameCheck.data.corp_code !== corpCode)
      ) {
        setErrors({ corp_name: "Corporate name or card name already exists" });
        toast.error("Corporate name or card name already exists");
        setIsLoading(false);
        return;
      }

      const tinCheck = await axios.post(
        `${BASE_URL}/corporates/check-tin`,
        {
          tin: formData.tin,
        },
        { headers: getAuthHeaders() }
      );

      if (
        tinCheck.data.exists &&
        (!isEditMode || tinCheck.data.corp_code !== corpCode)
      ) {
        setErrors({ tin: "TIN already exists for another corporate" });
        toast.error("TIN already exists for another corporate");
        setIsLoading(false);
        return;
      }

      const response = await axios.post(`${BASE_URL}/corporates`, formData, {
        headers: getAuthHeaders(),
      });
      toast.success(
        `Corporate ${isEditMode ? "updated" : "added"} successfully`
      );
      setTimeout(() => navigate("/corporate"), 2000);
    } catch (error) {
      let errorMessage = "An unexpected error occurred";
      const serverErrors = {};

      // Check if error response exists and handle different message formats
      if (error.response?.data?.message) {
        const message = error.response.data.message;
        if (typeof message === "object") {
          // Handle field-specific errors (e.g., {"corp_prefix": ["The corp prefix field must not be greater than 10 characters."]})
          Object.keys(message).forEach((field) => {
            serverErrors[field] = Array.isArray(message[field])
              ? message[field][0] // Take the first error message if it's an array
              : message[field];
          });
          // Use the first error message from the first field for toast
          const firstField = Object.keys(message)[0];
          errorMessage = Array.isArray(message[firstField])
            ? message[firstField][0]
            : message[firstField];
        } else if (typeof message === "string") {
          // Handle string error message
          errorMessage = message;
        }
      } else {
        // Fallback to generic error message
        errorMessage = error.message || errorMessage;
      }

      // Set field-specific errors if any
      if (Object.keys(serverErrors).length > 0) {
        setErrors(serverErrors);
      }

      // Display the error message
      toast.error(
        `Failed to ${isEditMode ? "update" : "add"} corporate: ${errorMessage}`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleFundFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFundForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const addFundContact = () => {
    if (!fundForm.fund_name || !fundForm.fund_mobile || !fundForm.fund_email) {
      toast.error("Please fill in all required fund contact fields.");
      return;
    }

    const exists = formData.fund_contacts.some(
      (contact) =>
        contact.fund_name.toLowerCase() === fundForm.fund_name.toLowerCase() &&
        contact.fund_mobile === fundForm.fund_mobile &&
        contact.fund_email.toLowerCase() === fundForm.fund_email.toLowerCase()
    );

    if (exists) {
      toast.error("This fund contact already exists.");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      fund_contacts: [
        ...prev.fund_contacts,
        {
          ...fundForm,
          fund_notif_sent: 0,
          fund_notif_datetime: null,
        },
      ],
    }));

    setFundForm({
      fund_name: "",
      fund_mobile: "",
      fund_email: "",
      fund_mgmt_flag: false,
    });
  };

  const removeFundContact = (index) => {
    setFormData((prev) => ({
      ...prev,
      fund_contacts: prev.fund_contacts.filter((_, i) => i !== index),
    }));
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const filteredFundContacts = formData.fund_contacts.filter((contact) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      (contact.fund_name &&
        contact.fund_name.toLowerCase().includes(searchLower)) ||
      (contact.fund_mobile && contact.fund_mobile.includes(searchLower)) ||
      (contact.fund_email &&
        contact.fund_email.toLowerCase().includes(searchLower))
    );
  });

  const totalItems = filteredFundContacts.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedFundContacts = filteredFundContacts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const fundContactsRequired = isTruthyFlag(formData.fund_mgmt_flag);

  return (
    <div className="container mx-auto p-4">
      <ToastContainer />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-lg shadow-lg p-6 max-w-1xl mx-auto"
      >
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">
            {isEditMode ? "Edit Corporate" : "Add New Corporate"}
          </h1>

          {/* <div className="mb-4"> */}
            <label className="block text-sm font-medium text-gray-700">
              Actual Corp Name:{" "}
              <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold mr-2 px-2.5 py-0.5 rounded">
                {formData.actual_corp_name == ""
                  ? "N/A"
                  : formData.actual_corp_name}
              </span>
            </label>
          {/* </div> */}

          {/* <div className="mb-4"> */}
            <label className="block text-sm font-medium text-gray-700">
              Corp Name:{" "}
              <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold mr-2 px-2.5 py-0.5 rounded">
                {formData.corp_name == "" ? "N/A" : formData.corp_name}
              </span>
            </label>
          {/* </div> */}

          <button
            onClick={() => navigate("/corporate")}
            className="flex items-center space-x-1 text-gray-600 hover:text-gray-800"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            <span>Back to List</span>
          </button>
        </div>

        <div className="sticky top-0 z-30 -mx-6 mb-6 border-y border-gray-200 bg-white/95 px-6 shadow-sm backdrop-blur">
          <nav className="-mb-px flex gap-6 overflow-x-auto">
            {[
              "information",
              "bank_details",
              "address",
              "contact",
              "BIR",
              "corporate_fund",
            ].map((tab) => (
              <button
                type="button"
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`${
                  activeTab === tab
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm capitalize`}
              >
                {tab.replace("_", " ")}
              </button>
            ))}
          </nav>
        </div>

        <form onSubmit={handleSubmit}>
          {activeTab === "information" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold mr-1 px-2.5 py-0.5 rounded">
                      New
                    </span>
                    Actual Corporate Name *
                  </label>
                  <input
                    type="text"
                    name="actual_corp_name"
                    value={formData.actual_corp_name}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border ${
                      errors.actual_corp_name
                        ? "border-red-500"
                        : "border-gray-200"
                    } rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500`}
                    // placeholder="Enter actual corporate name (remove the prefix)"
                    placeholder="e.g. ATLANTIC GRAINS INC."
                  />
                  <span className="inline-block bg-red-100 text-red-800 text-xs font-semibold mr-1 px-0.5 py-0.5 rounded">
                      Notes: Without corporate prefix, e.g. ATLANTIC GRAINS INC.
                  </span>
                  {errors.actual_corp_name && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.actual_corp_name}
                    </p>
                  )}
                  
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Corporate Name *
                  </label>
                  <input
                    type="text"
                    name="corp_name"
                    value={formData.corp_name}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border ${
                      errors.corp_name ? "border-red-500" : "border-gray-200"
                    } rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500`}
                    // placeholder="Enter corporate name"
                    placeholder="e.g. LIBERTY - ATLANTIC GRAINS INC."
                  />
                  <span className="inline-block bg-red-100 text-red-800 text-xs font-semibold mr-1 px-0.5 py-0.5 rounded">
                      Notes: With corporate prefix, e.g. LIBERTY - ATLANTIC GRAINS INC.
                    </span>
                  {errors.corp_name && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.corp_name}
                    </p>
                  )}
                  
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Name in the Card *
                  </label>
                  <input
                    type="text"
                    name="corp_card_name"
                    value={formData.corp_card_name}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border ${
                      errors.corp_card_name
                        ? "border-red-500"
                        : "border-gray-200"
                    } rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500`}
                    placeholder="Enter name in the card"
                  />
                  {errors.corp_card_name && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.corp_card_name}
                    </p>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Licensed Name
                  </label>
                  <input
                    type="text"
                    name="licensed_name"
                    value={formData.licensed_name}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Enter licensed/merchant name"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Industry *
                  </label>
                  <Select
                    name="corp_industry_code"
                    value={industries.find(
                      (i) => i.value === formData.corp_industry_code
                    )}
                    onChange={(option) =>
                      handleSelectChange("corp_industry_code", option)
                    }
                    options={industries} // Updated to use industries directly, including "Others"
                    isSearchable
                    className="text-sm"
                    placeholder="Select an industry"
                  />
                  {errors.corp_industry_code && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.corp_industry_code}
                    </p>
                  )}
                </div>
                {formData.corp_industry_code === "O" && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Others *
                    </label>
                    <input
                      type="text"
                      name="corp_industry_others"
                      value={formData.corp_industry_others}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full border ${
                        errors.corp_industry_others
                          ? "border-red-500"
                          : "border-gray-200"
                      } rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500`}
                      placeholder="Enter industry"
                    />
                    {errors.corp_industry_others && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.corp_industry_others}
                      </p>
                    )}
                  </div>
                )}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Account Type *
                  </label>
                  <select
                    name="corp_type_code"
                    value={formData.corp_type_code}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border ${
                      errors.corp_type_code
                        ? "border-red-500"
                        : "border-gray-200"
                    } rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500`}
                  >
                    <option value="">Choose account type</option>
                    <option value="C">Corporate</option>
                    <option value="G">Group</option>
                    <option value="I">Individual</option>
                  </select>
                  {errors.corp_type_code && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.corp_type_code}
                    </p>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Philhealth *
                  </label>
                  <input
                    type="text"
                    name="phic_no"
                    value={formData.phic_no}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border ${
                      errors.phic_no ? "border-red-500" : "border-gray-200"
                    } rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500`}
                    placeholder="Enter philhealth number"
                  />
                  {errors.phic_no && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.phic_no}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Account Status
                  </label>
                  <input
                    type="text"
                    value="Pending"
                    disabled
                    className="mt-1 block w-full border border-gray-200 rounded-md p-2 text-sm bg-gray-100"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Acceptance Age *
                  </label>
                  <input
                    type="number"
                    name="min_age"
                    value={formData.min_age}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border ${
                      errors.min_age ? "border-red-500" : "border-gray-200"
                    } rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500`}
                    placeholder="Enter age"
                    min="0"
                    maxLength="2"
                  />
                  {errors.min_age && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.min_age}
                    </p>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Termination Age *
                  </label>
                  <input
                    type="number"
                    name="max_age"
                    value={formData.max_age}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border ${
                      errors.max_age ? "border-red-500" : "border-gray-200"
                    } rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500`}
                    placeholder="Enter age"
                    min="0"
                    maxLength="3"
                  />
                  {errors.max_age && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.max_age}
                    </p>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Benefit Layer *
                  </label>
                  <select
                    name="cobrand_layer"
                    value={formData.cobrand_layer}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border ${
                      errors.cobrand_layer
                        ? "border-red-500"
                        : "border-gray-200"
                    } rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500`}
                  >
                    <option value="1">1st Layer</option>
                    <option value="2">2nd Layer</option>
                  </select>
                  {errors.cobrand_layer && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.cobrand_layer}
                    </p>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Remarks
                  </label>
                  <textarea
                    name="remarks"
                    value={formData.remarks}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    rows="3"
                  ></textarea>
                </div>
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="cobrand_tag"
                      checked={formData.cobrand_tag}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Co-brand Tag
                    </span>
                  </label>
                </div>
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="spec_exp"
                      checked={formData.spec_exp}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Special Case (Independent member's expiration e.g. HC&D)
                    </span>
                  </label>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Effective Date (from) *
                  </label>
                  <input
                    type="date"
                    name="effective_date"
                    value={formData.effective_date}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border ${
                      errors.effective_date
                        ? "border-red-500"
                        : "border-gray-200"
                    } rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500`}
                  />
                  {errors.effective_date && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.effective_date}
                    </p>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Effective Date (to)
                  </label>
                  <input
                    type="date"
                    name="effective_date_to"
                    value={formData.effective_date_to}
                    readOnly
                    className="mt-1 block w-full border border-gray-200 rounded-md p-2 text-sm bg-gray-100"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Expiry Duration
                  </label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="expiry_duration"
                        value="m"
                        checked={formData.expiry_duration === "m"}
                        onChange={() => {
                          setAutoCalculateExpiryDate(true);
                          setFormData((prev) => ({
                            ...prev,
                            expiry_duration: "m",
                          }));
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        6 months
                      </span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="expiry_duration"
                        value="y"
                        checked={formData.expiry_duration === "y"}
                        onChange={() => {
                          setAutoCalculateExpiryDate(true);
                          setFormData((prev) => ({
                            ...prev,
                            expiry_duration: "y",
                          }));
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        1 year
                      </span>
                    </label>
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Expiry Date *
                  </label>
                  <input
                    type="date"
                    name="expiry_date"
                    value={formData.expiry_date}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border ${
                      errors.expiry_date ? "border-red-500" : "border-gray-200"
                    } rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500`}
                  />
                  {errors.expiry_date && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.expiry_date}
                    </p>
                  )}
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Corporate Logo
                  </label>
                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    onChange={(e) => handleFileUpload(e, "corporate_logo")}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  <UploadedDocumentUrlPanel
                    url={formData.corporate_logo_signed_url}
                    downloadLabel="Open Corporate Logo"
                    onRegenerate={() =>
                      handleRegenerateUrl(
                        formData.corporate_logo_upload_id,
                        "corporate_logo"
                      )
                    }
                  />
                  {logoPreview && (
                    <div className="mt-2">
                      <img
                        src={logoPreview}
                        alt="Corporate Logo"
                        className="w-48 h-24 object-contain border border-gray-200 rounded-md"
                      />
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Canvas size should be 600x300 pixels in PNG format
                  </p>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    SOB File
                  </label>
                  <input
                    type="file"
                    accept=".gif,.png,.jpg,.jpeg,.bmp,.pdf,.csv,.doc,.docx,.xlsx,.xls"
                    onChange={(e) => handleFileUpload(e, "sob_file")}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  <UploadedDocumentUrlPanel
                    url={formData.sob_file_signed_url}
                    downloadLabel="Download SOB File"
                    onRegenerate={() =>
                      handleRegenerateUrl(formData.sob_file_upload_id, "sob_file")
                    }
                  />
                </div>



              </div>
            </div>
          )}

          {activeTab === "bank_details" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Account Name *
                  </label>
                  <input
                    type="text"
                    name="bank_acc_name"
                    value={formData.bank_acc_name}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border ${
                      errors.bank_acc_name
                        ? "border-red-500"
                        : "border-gray-200"
                    } rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500`}
                    placeholder="Enter bank account name"
                  />
                  {errors.bank_acc_name && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.bank_acc_name}
                    </p>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Account Number *
                  </label>
                  <input
                    type="text"
                    name="bank_no"
                    value={formData.bank_no}
                    onChange={handleInputChange}
                    maxLength={BANK_ACCOUNT_NUMBER_MAX_LENGTH}
                    className={`mt-1 block w-full border ${
                      errors.bank_no ? "border-red-500" : "border-gray-200"
                    } rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500`}
                    placeholder="Enter account number"
                  />
                  {errors.bank_no && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.bank_no}
                    </p>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Bank Name *
                  </label>
                  <input
                    type="text"
                    name="bank_name"
                    value={formData.bank_name}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border ${
                      errors.bank_name ? "border-red-500" : "border-gray-200"
                    } rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500`}
                    placeholder="Enter bank name"
                  />
                  {errors.bank_name && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.bank_name}
                    </p>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Bank Branch *
                  </label>
                  <input
                    type="text"
                    name="bank_branch"
                    value={formData.bank_branch}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border ${
                      errors.bank_branch ? "border-red-500" : "border-gray-200"
                    } rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500`}
                    placeholder="Enter bank branch"
                  />
                  {errors.bank_branch && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.bank_branch}
                    </p>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Billing Due Date *
                  </label>
                  <input
                    type="date"
                    name="bill_duedate"
                    value={formData.bill_duedate}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border ${
                      errors.bill_duedate ? "border-red-500" : "border-gray-200"
                    } rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500`}
                    placeholder="Enter billing due date (yyyy-mm-dd)"
                  />
                  {errors.bill_duedate && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.bill_duedate}
                    </p>
                  )}
                </div>
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="auto_deduct_tag"
                      checked={formData.auto_deduct_tag}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Auto Deduct
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {activeTab === "address" && (
            <div>
              <h2 className="text-lg font-semibold mb-4">Corporate Address</h2>
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Street No./Building *
                  </label>
                  <input
                    type="text"
                    name="corp_streetno_bldg"
                    value={formData.corp_streetno_bldg}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border ${
                      errors.corp_streetno_bldg
                        ? "border-red-500"
                        : "border-gray-200"
                    } rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500`}
                    placeholder="Enter street no./building"
                  />
                  {errors.corp_streetno_bldg && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.corp_streetno_bldg}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Region *
                  </label>
                  <Select
                    name="corp_region"
                    value={corpRegions.find(
                      (r) => r.value === formData.corp_region
                    )}
                    onChange={(option) =>
                      handleSelectChange("corp_region", option)
                    }
                    options={[
                      { value: "", label: "Select a region" },
                      ...corpRegions,
                    ]}
                    isSearchable
                    className="text-sm"
                    isLoading={corpAddressLoading.region}
                    isDisabled={corpAddressLoading.region}
                  />
                  {errors.corp_region && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.corp_region}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Province *
                  </label>
                  <Select
                    name="corp_province"
                    value={corpProvinces.find(
                      (p) => p.value === formData.corp_province
                    )}
                    onChange={(option) =>
                      handleSelectChange("corp_province", option)
                    }
                    options={[
                      { value: "", label: "Select a province" },
                      ...corpProvinces,
                    ]}
                    isSearchable
                    className="text-sm"
                    isLoading={corpAddressLoading.province}
                    isDisabled={
                      !formData.corp_region || corpAddressLoading.province
                    }
                  />
                  {errors.corp_province && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.corp_province}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Town/City *
                  </label>
                  <Select
                    name="corp_towncity"
                    value={corpTownCities.find(
                      (c) => c.value === formData.corp_towncity
                    )}
                    onChange={(option) =>
                      handleSelectChange("corp_towncity", option)
                    }
                    options={[
                      { value: "", label: "Select a town/city" },
                      ...corpTownCities,
                    ]}
                    isSearchable
                    className="text-sm"
                    isLoading={corpAddressLoading.towncity}
                    isDisabled={
                      !formData.corp_province || corpAddressLoading.towncity
                    }
                  />
                  {errors.corp_towncity && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.corp_towncity}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Barangay *
                  </label>
                  <Select
                    name="corp_barangay"
                    value={
                      corpBarangays.find(
                        (b) => b.value === formData.corp_barangay
                      ) || null
                    }
                    onChange={(option) =>
                      handleSelectChange("corp_barangay", option)
                    }
                    options={[
                      { value: "", label: "Select a barangay" },
                      ...corpBarangays,
                    ]}
                    isSearchable
                    className="text-sm"
                    isLoading={corpAddressLoading.barangay}
                    isDisabled={
                      !formData.corp_towncity || corpAddressLoading.barangay
                    }
                  />
                  {errors.corp_barangay && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.corp_barangay}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Postal Code
                  </label>
                  <input
                    type="text"
                    name="corp_postalcode"
                    value={formData.corp_postalcode}
                    readOnly
                    className="mt-1 block w-full border border-gray-200 rounded-md bg-gray-100 p-2 text-sm text-gray-700"
                    placeholder="Auto-populated postal code"
                  />
                </div>
              </div>
              <h2 className="text-lg font-semibold mb-4">Billing Address</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Street No./Building *
                  </label>
                  <input
                    type="text"
                    name="bill_streetno_bldg"
                    value={formData.bill_streetno_bldg}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border ${
                      errors.bill_streetno_bldg
                        ? "border-red-500"
                        : "border-gray-200"
                    } rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500`}
                    placeholder="Enter street no./building"
                  />
                  {errors.bill_streetno_bldg && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.bill_streetno_bldg}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Region *
                  </label>
                  <Select
                    name="bill_region"
                    value={billRegions.find(
                      (r) => r.value === formData.bill_region
                    )}
                    onChange={(option) =>
                      handleSelectChange("bill_region", option)
                    }
                    options={[
                      { value: "", label: "Select a region" },
                      ...billRegions,
                    ]}
                    isSearchable
                    className="text-sm"
                    isLoading={billAddressLoading.region}
                    isDisabled={billAddressLoading.region}
                  />
                  {errors.bill_region && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.bill_region}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Province *
                  </label>
                  <Select
                    name="bill_province"
                    value={billProvinces.find(
                      (p) => p.value === formData.bill_province
                    )}
                    onChange={(option) =>
                      handleSelectChange("bill_province", option)
                    }
                    options={[
                      { value: "", label: "Select a province" },
                      ...billProvinces,
                    ]}
                    isSearchable
                    className="text-sm"
                    isLoading={billAddressLoading.province}
                    isDisabled={
                      !formData.bill_region || billAddressLoading.province
                    }
                  />
                  {errors.bill_province && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.bill_province}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Town/City *
                  </label>
                  <Select
                    name="bill_towncity"
                    value={billTownCities.find(
                      (c) => c.value === formData.bill_towncity
                    )}
                    onChange={(option) =>
                      handleSelectChange("bill_towncity", option)
                    }
                    options={[
                      { value: "", label: "Select a town/city" },
                      ...billTownCities,
                    ]}
                    isSearchable
                    className="text-sm"
                    isLoading={billAddressLoading.towncity}
                    isDisabled={
                      !formData.bill_province || billAddressLoading.towncity
                    }
                  />
                  {errors.bill_towncity && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.bill_towncity}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Barangay *
                  </label>
                  <Select
                    name="bill_barangay"
                    value={billBarangays.find(
                      (b) => b.value === formData.bill_barangay
                    )}
                    onChange={(option) =>
                      handleSelectChange("bill_barangay", option)
                    }
                    options={[
                      { value: "", label: "Select a barangay" },
                      ...billBarangays,
                    ]}
                    isSearchable
                    className="text-sm"
                    isLoading={billAddressLoading.barangay}
                    isDisabled={
                      !formData.bill_towncity || billAddressLoading.barangay
                    }
                  />
                  {errors.bill_barangay && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.bill_barangay}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Postal Code
                  </label>
                  <input
                    type="text"
                    name="bill_postalcode"
                    value={formData.bill_postalcode}
                    readOnly
                    className="mt-1 block w-full border border-gray-200 rounded-md bg-gray-100 p-2 text-sm text-gray-700"
                    placeholder="Auto-populated postal code"
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === "contact" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    CEO Name
                  </label>
                  <input
                    type="text"
                    name="ceo_name"
                    value={formData.ceo_name}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Enter CEO name"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    COO Name
                  </label>
                  <input
                    type="text"
                    name="coo_name"
                    value={formData.coo_name}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Enter COO name"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Contact Person *
                  </label>
                  <input
                    type="text"
                    name="corp_careof"
                    value={formData.corp_careof}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border ${
                      errors.corp_careof ? "border-red-500" : "border-gray-200"
                    } rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500`}
                    placeholder="Enter contact person"
                  />
                  {errors.corp_careof && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.corp_careof}
                    </p>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Designation *
                  </label>
                  <input
                    type="text"
                    name="corp_careof_designation"
                    value={formData.corp_careof_designation}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border ${
                      errors.corp_careof_designation
                        ? "border-red-500"
                        : "border-gray-200"
                    } rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500`}
                    placeholder="Enter designation"
                  />
                  {errors.corp_careof_designation && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.corp_careof_designation}
                    </p>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Telephone Number
                  </label>
                  <input
                    type="text"
                    name="tel_no"
                    value={formData.tel_no}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Enter telephone number"
                  />
                </div>
              </div>
              <div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Fax Number
                  </label>
                  <input
                    type="text"
                    name="fax_no"
                    value={formData.fax_no}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Enter fax number"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Mobile Number
                  </label>
                  <input
                    type="text"
                    name="mobtel"
                    value={formData.mobtel}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Enter mobile number"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email_add"
                    value={formData.email_add}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Enter email address"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Email Recipient (HR) *
                  </label>
                  <input
                    type="email"
                    name="email_recipient"
                    value={formData.email_recipient}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border ${
                      errors.email_recipient
                        ? "border-red-500"
                        : "border-gray-200"
                    } rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500`}
                    placeholder="Enter email recipient"
                  />
                  {errors.email_recipient && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.email_recipient}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === "BIR" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    TIN Number *
                  </label>
                  <input
                    type="text"
                    name="tin"
                    value={formData.tin}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border ${
                      errors.tin ? "border-red-500" : "border-gray-200"
                    } rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500`}
                    placeholder="Enter TIN number"
                  />
                  {errors.tin && (
                    <p className="text-red-500 text-xs mt-1">{errors.tin}</p>
                  )}
                </div>
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="withheld"
                      checked={formData.withheld}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Withheld
                    </span>
                  </label>
                </div>
                {formData.withheld && (
                  <>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Representative's TIN *
                      </label>
                      <input
                        type="text"
                        name="rep_tin"
                        value={formData.rep_tin}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full border ${
                          errors.rep_tin ? "border-red-500" : "border-gray-200"
                        } rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500`}
                        placeholder="Enter representative's TIN"
                      />
                      {errors.rep_tin && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.rep_tin}
                        </p>
                      )}
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Representative's Name *
                      </label>
                      <input
                        type="text"
                        name="rep_name"
                        value={formData.rep_name}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full border ${
                          errors.rep_name ? "border-red-500" : "border-gray-200"
                        } rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500`}
                        placeholder="Enter representative name"
                      />
                      {errors.rep_name && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.rep_name}
                        </p>
                      )}
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Electronic Signature File *
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          handleFileUpload(e, "electronic_signature")
                        }
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                      <UploadedDocumentUrlPanel
                        url={formData.electronic_signature_signed_url}
                        downloadLabel="Download Signature"
                        onRegenerate={() =>
                          handleRegenerateUrl(
                            formData.electronic_signature_upload_id,
                            "electronic_signature"
                          )
                        }
                      />
                      {errors.electronic_signature_upload_id && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.electronic_signature_upload_id}
                        </p>
                      )}
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Position *
                      </label>
                      <input
                        type="text"
                        name="rep_position"
                        value={formData.rep_position}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full border ${
                          errors.rep_position
                            ? "border-red-500"
                            : "border-gray-200"
                        } rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500`}
                        placeholder="Enter position"
                      />
                      {errors.rep_position && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.rep_position}
                        </p>
                      )}
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Secretary Certificate File *
                      </label>
                      <input
                        type="file"
                        accept=".gif,.png,.jpg,.jpeg,.bmp,.pdf,.doc,.docx"
                        onChange={(e) =>
                          handleFileUpload(e, "secretary_cert")
                        }
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                      <UploadedDocumentUrlPanel
                        url={formData.secretary_cert_signed_url}
                        downloadLabel="Download Certificate"
                        onRegenerate={() =>
                          handleRegenerateUrl(
                            formData.secretary_cert_upload_id,
                            "secretary_cert"
                          )
                        }
                      />
                      {errors.secretary_cert_upload_id && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.secretary_cert_upload_id}
                        </p>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {activeTab === "corporate_fund" && (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              {/* Left Side: Fund Contacts Management */}
              <div className="md:col-span-5">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold mr-1 px-2.5 py-0.5 rounded">
                      New
                    </span>
                    Corporate Prefix
                    
                  </label>
                  <CreatableSelect
                    name="corp_prefix"
                    value={corpPrefixes.find(
                      (p) => p.value === formData.corp_prefix
                    )}
                    onChange={(option) =>
                      handleSelectChange("corp_prefix", option)
                    }
                    options={[
                      { value: "", label: "Select a prefix" },
                      ...corpPrefixes,
                    ]}
                    isSearchable
                    className="text-sm mt-1"
                  />
                  <span className="inline-block bg-red-100 text-red-800 text-xs font-semibold mr-1 px-2.5 py-0.5 rounded">
                    Notes: Update the corporate prefix accordingly!
                  </span>
                  {errors.corp_prefix && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.corp_prefix}
                    </p>
                  )}
                  
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Main/Sub Account *
                  </label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="main_sub"
                        value="main"
                        checked={formData.main_sub === "main"}
                        onChange={() =>
                          setFormData((prev) => ({ ...prev, main_sub: "main" }))
                        }
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Main
                      </span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="main_sub"
                        value="sub"
                        checked={formData.main_sub === "sub"}
                        onChange={() =>
                          setFormData((prev) => ({ ...prev, main_sub: "sub" }))
                        }
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Sub
                      </span>
                    </label>
                  </div>
                  {errors.main_sub && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.main_sub}
                    </p>
                  )}
                </div>
                {formData.main_sub === "sub" && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Select Main Corporation *
                    </label>
                    <Select
                      name="main_corp"
                      value={mainCorporations.find(
                        (c) => c.value === formData.main_corp
                      )}
                      onChange={(option) =>
                        handleSelectChange("main_corp", option)
                      }
                      options={[
                        { value: "", label: "Select a main corporation" },
                        ...mainCorporations,
                      ]}
                      isSearchable
                      className="text-sm"
                    />
                    {errors.main_corp && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.main_corp}
                      </p>
                    )}
                  </div>
                )}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Cash Bond
                  </label>
                  <input
                    type="number"
                    name="cash_bond"
                    value={formData.cash_bond}
                    onChange={handleInputChange}
                    disabled={formData.main_sub === "sub"}
                    className={`mt-1 block w-full border ${
                      errors.cash_bond ? "border-red-500" : "border-gray-200"
                    } rounded-md p-2 text-sm ${
                      formData.main_sub === "sub"
                        ? "bg-gray-100"
                        : "focus:outline-none focus:ring-1 focus:ring-blue-500"
                    }`}
                    placeholder="Enter cash bond"
                    style={{ textAlign: "right" }}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Revolving Fund *
                  </label>
                  <input
                    type="number"
                    name="revolving_fund_amt"
                    value={formData.revolving_fund_amt}
                    onChange={handleInputChange}
                    disabled={formData.main_sub === "sub"}
                    className={`mt-1 block w-full border ${
                      errors.revolving_fund_amt
                        ? "border-red-500"
                        : "border-gray-200"
                    } rounded-md p-2 text-sm ${
                      formData.main_sub === "sub"
                        ? "bg-gray-100"
                        : "focus:outline-none focus:ring-1 focus:ring-blue-500"
                    }`}
                    placeholder="Enter revolving fund"
                    style={{ textAlign: "right" }}
                  />
                  {errors.revolving_fund_amt && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.revolving_fund_amt}
                    </p>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Utilized Amount
                  </label>
                  <input
                    type="number"
                    name="utilized_amt"
                    value={formData.utilized_amt}
                    readOnly
                    className="mt-1 block w-full border border-gray-200 rounded-md p-2 text-sm bg-gray-100"
                    style={{ textAlign: "right" }}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Stale Amount
                  </label>
                  <input
                    type="number"
                    name="stale_amt"
                    value={formData.stale_amt}
                    readOnly
                    className="mt-1 block w-full border border-gray-200 rounded-md p-2 text-sm bg-gray-100"
                    style={{ textAlign: "right" }}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    First Warning Amount
                  </label>
                  <input
                    type="number"
                    name="first_warning_amount"
                    value={formData.first_warning_amount}
                    onChange={handleInputChange}
                    disabled={formData.main_sub === "sub"}
                    className={`mt-1 block w-full border ${
                      errors.first_warning_amount
                        ? "border-red-500"
                        : "border-gray-200"
                    } rounded-md p-2 text-sm ${
                      formData.main_sub === "sub"
                        ? "bg-gray-100"
                        : "focus:outline-none focus:ring-1 focus:ring-blue-500"
                    }`}
                    placeholder="Enter warning amount"
                    style={{ textAlign: "right" }}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Threshold *
                  </label>
                  <input
                    type="number"
                    name="threshold_amt"
                    value={formData.threshold_amt}
                    onChange={handleInputChange}
                    disabled={formData.main_sub === "sub"}
                    className={`mt-1 block w-full border ${
                      errors.threshold_amt
                        ? "border-red-500"
                        : "border-gray-200"
                    } rounded-md p-2 text-sm ${
                      formData.main_sub === "sub"
                        ? "bg-gray-100"
                        : "focus:outline-none focus:ring-1 focus:ring-blue-500"
                    }`}
                    placeholder="Enter threshold"
                    style={{ textAlign: "right" }}
                  />
                  {errors.threshold_amt && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.threshold_amt}
                    </p>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Remaining Fund
                  </label>
                  <input
                    type="number"
                    name="remaining_fund"
                    value={formData.revolving_fund_amt - formData.utilized_amt}
                    readOnly
                    className="mt-1 block w-full border border-gray-200 rounded-md p-2 text-sm bg-gray-100"
                    style={{ textAlign: "right" }}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Admin Fee
                  </label>
                  <input
                    type="number"
                    name="admin_fee"
                    value={formData.admin_fee}
                    onChange={handleInputChange}
                    disabled={formData.main_sub === "sub"}
                    className={`mt-1 block w-full border ${
                      errors.admin_fee ? "border-red-500" : "border-gray-200"
                    } rounded-md p-2 text-sm ${
                      formData.main_sub === "sub"
                        ? "bg-gray-100"
                        : "focus:outline-none focus:ring-1 focus:ring-blue-500"
                    }`}
                    placeholder="Enter admin fee"
                    style={{ textAlign: "right" }}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Payment Setup
                  </label>
                  <select
                    name="payment_setup"
                    value={formData.payment_setup}
                    onChange={handleInputChange}
                    disabled={formData.main_sub === "sub"}
                    className={`mt-1 block w-full border ${
                      errors.payment_setup
                        ? "border-red-500"
                        : "border-gray-200"
                    } rounded-md p-2 text-sm ${
                      formData.main_sub === "sub"
                        ? "bg-gray-100"
                        : "focus:outline-none focus:ring-1 focus:ring-blue-500"
                    }`}
                  >
                    <option value="0">Select payment setup</option>
                    <option value="1">Blanket</option>
                    <option value="2">Non-Blanket</option>
                  </select>
                </div>
              </div>
              

              {/* Right Side: Existing Corporate Fund Fields (unchanged) */}
              <div className="md:col-span-7">
                
                  <div className="flex items-center mb-1">
                    <input
                      type="checkbox"
                      name="fund_mgmt_flag"
                      checked={formData.fund_mgmt_flag}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          fund_mgmt_flag: e.target.checked,
                        }));
                        console.log("fund_mgmt_flag:", e.target.checked); // For debugging
                      }}
                      className="mr-2"
                      disabled={isLoading}
                    />
                    <span className="inline-block bg-yellow-100 text-yellow-800 text-xs font-semibold mr-1 px-1.5 py-0.5 rounded">
                      Fund Management Flag
                    </span>
                    {/* <label className="text-sm font-medium text-gray-700">
                      
                    </label> */}
                  </div>

                <h3 className="text-lg font-semibold text-gray-800">
                  Fund Contacts
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Fund Name{fundContactsRequired ? " *" : ""}
                    </label>
                    <input
                      type="text"
                      name="fund_name"
                      value={fundForm.fund_name}
                      onChange={handleFundFormChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Enter fund name"
                      disabled={isLoading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Fund Mobile{fundContactsRequired ? " *" : ""}
                    </label>
                    <input
                      type="text"
                      name="fund_mobile"
                      value={fundForm.fund_mobile}
                      onChange={handleFundFormChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Enter fund mobile"
                      disabled={isLoading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Fund Email{fundContactsRequired ? " *" : ""}
                    </label>
                    <input
                      type="email"
                      name="fund_email"
                      value={fundForm.fund_email}
                      onChange={handleFundFormChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Enter fund email"
                      disabled={isLoading}
                    />
                  </div>

                  {/* <div className="flex items-center mt-6">
                    <input
                      type="checkbox"
                      name="fund_mgmt_flag"
                      checked={fundForm.fund_mgmt_flag}
                      onChange={handleFundFormChange}
                      className="mr-2"
                      disabled={isLoading}
                    />
                    <label className="text-sm font-medium text-gray-700">
                      Fund Management Flag
                    </label>
                  </div> */}

                </div>
                <button
                  type="button"
                  onClick={addFundContact}
                  className="mb-1 mt-2 flex items-center space-x-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm"
                  disabled={isLoading}
                >
                  <PlusIcon className="h-5 w-5" />
                  <span>Add Fund Contact</span>
                </button>
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center space-x-2 relative">
                    <label className="text-sm font-medium text-gray-700">
                      Search:
                    </label>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-8 pr-4 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Search by name, mobile or email"
                    />
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-10 top-1/2 transform -translate-y-1/2" />
                  </div>
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700">
                      Show entries:
                    </label>
                    <select
                      value={itemsPerPage}
                      onChange={handleItemsPerPageChange}
                      className="border border-gray-200 rounded-md px-2 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                          Mobile
                        </th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        {/* <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                          Mgmt Flag
                        </th> */}
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                          Notif Sent
                        </th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                          Notif Datetime
                        </th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {paginatedFundContacts.map((contact, index) => (
                        <tr key={index}>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {contact.fund_name || "N/A"}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {contact.fund_mobile || "N/A"}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {contact.fund_email || "N/A"}
                          </td>
                          {/* <td className="px-4 py-2 text-sm text-gray-900">
                            {contact.fund_mgmt_flag ? "Yes" : "No"}
                          </td> */}
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {contact.fund_notif_sent ? "Yes" : "No"}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {contact.fund_notif_datetime || "N/A"}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            <button
                              type="button"
                              onClick={() =>
                                removeFundContact(
                                  (currentPage - 1) * itemsPerPage + index
                                )
                              }
                              className="flex items-center space-x-1 text-red-600 hover:text-red-800 text-sm"
                              disabled={isLoading}
                            >
                              <TrashIcon className="h-5 w-5" />
                              <span>Remove</span>
                            </button>
                          </td>
                        </tr>
                      ))}
                      {paginatedFundContacts.length === 0 && (
                        <tr>
                          <td
                            colSpan="7"
                            className="px-4 py-2 text-sm text-gray-500 text-center"
                          >
                            {searchQuery
                              ? "No matching fund contacts found."
                              : "No fund contacts added."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-between items-center mt-4">
                  <div className="text-sm text-gray-600">
                    Showing{" "}
                    {totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}{" "}
                    to {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                    {totalItems} entries
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1 || isLoading}
                      className="px-3 py-2 border border-gray-200 rounded-md text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-2 text-sm text-gray-600">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || isLoading}
                      className="px-3 py-2 border border-gray-200 rounded-md text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 flex justify-end border-t border-gray-200 pt-5">
            <button
              type="submit"
              className="inline-flex min-w-[170px] items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <ArrowPathIcon className="h-5 w-5 animate-spin" />
                  <span>{isEditMode ? "Updating..." : "Saving..."}</span>
                </>
              ) : (
                <>
                  {isEditMode ? (
                    <PencilSquareIcon className="h-5 w-5" />
                  ) : (
                    <PlusCircleIcon className="h-5 w-5" />
                  )}
                  <span>{isEditMode ? "Update Corporate" : "Add Corporate"}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default CorporateEnrollmentForm;
